<template>
  <!-- START LOADING -->
  <template v-if="isApplicationBundleLoading">
    <div v-show="getApplicationBundleSearchTerm" class="flex flex-row py-1 px-1">
      <p class="text-sm md:text-base">
        Searching for results matching "{{ getApplicationBundleSearchTerm }}" results...
      </p>
      <img src="" />
    </div>

    <div v-if="!getApplicationBundleSearchTerm"><Loading /></div>
  </template>
  <!-- END LOADING -->

  <!-- START DISPLAY BUNDLE TABLE -->
  <template v-if="!isApplicationBundleLoading">
    <!-- START HAS BUNDLES DISPLAY -->
    <template v-if="getApplicationBundles && getApplicationBundles.length">
      <!-- START SEARCH RESULTS ELEMENT -->
      <div v-if="getApplicationBundleSearchTerm" class="flex flex-row py-2 px-1">
        <!-- START DESKTOP -->
        <div class="hidden md:flex items-center">
          <p>
            Search results matching "{{ getApplicationBundleSearchTerm }}", showing
            {{ getApplicationBundles.length }} of
            {{ getApplicationBundleTotalCount }}
          </p>
        </div>
        <!-- END DESKTOP -->

        <!-- START MOBILE -->
        <div class="flex flex-col md:hidden px-1">
          <p class="text-sm">Search results matching "{{ getApplicationBundleSearchTerm }}"</p>
          <div class="flex flex-row items-center py-1">
            <p class="text-xs italic text-brand-dark-gray">
              showing {{ getApplicationBundles.length }} of
              {{ getApplicationBundleTotalCount }} results
            </p>
            <div
              class="md:hidden py-0.5 px-1 flex items-center bg-white border border-brand-mid-gray rounded mx-3"
              @click="clearSearch"
            >
              <p class="text-sm text-brand-dark-gray">clear</p>
            </div>
          </div>
        </div>
      </div>
      <!-- END SEARCH RESULTS ELEMENT -->

      <!-- START APP BUNDLE ROW -->
      <div v-for="(bundles, date) in formattedBundles" :key="date">
        <h5 class="text-sm md:text-base px-3 pb-3 pt-2 md:pt-5">
          {{ formatDate(date) }}
        </h5>

        <div
          v-for="bundle in bundles"
          :key="bundle.slug"
          class="flex flex-col md:rounded shadow-sm bg-white py-1 px-3 md:py-3 md:px-5 w-full my-0.5 md:my-3 bundle-row"
          :class="[bundle.applications.length > 1 ? 'border-4 border-brand-green md:border-0' : '']"
        >
          <div
            class="flex flex-row w-full md:py-2 app-row cursor-pointer relative"
            v-for="(app, index) in bundle.applications"
            :key="app.id"
            :class="[
              ['sent to applicant', 'invitation bounced'].includes(app.status) ? 'cursor-none' : ''
            ]"
            @mouseenter="app.tooltip_toggled = true"
            @mouseleave="app.tooltip_toggled = false"
            @click="goToViewApp(app)"
          >
            <!-- START DESKTOP APP ROW -->
            <div class="hidden md:flex flex-col w-full relative">
              <div
                v-if="
                  app.status === 'partial' &&
                  getCurrentUserContext !== 'applicant' &&
                  app.tooltip_toggled
                "
                class="absolute left-24 z-30 w-64 py-2 px-2 text-xs mt-2 bg-brand-gray text-gray-100 px-1 rounded shadow-xl"
              >
                This application has not been completed by the applicant.
              </div>
              <div class="px-16 my-4" v-if="index !== 0">
                <div class="border border-brand-mid-gray"></div>
              </div>

              <div class="flex w-full">
                <div
                  class="w-1/12 flex items-center relative"
                  :class="[index > 0 ? 'border-t-1 border-brand-mid-gray' : '']"
                >
                  <template v-if="index === 0">
                    <input
                      type="checkbox"
                      class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
                      v-model="bundle.selected"
                      @click="$event.stopPropagation()"
                      @input="selectBundle(bundle)"
                    />
                  </template>
                  <template v-if="bundle.applications.length > 1">
                    <template v-if="index === 0">
                      <div
                        v-if="bundle.selected"
                        class="absolute border border-brand-green top-16 lg:top-8 left-2 h-24"
                      ></div>
                    </template>
                    <template v-if="index > 0 && index < bundle.applications.length - 1">
                      <div
                        v-if="bundle.selected"
                        class="absolute border-l border-r border-brand-green lg:-top-5 left-2 h-44 lg:h-36"
                      ></div>
                    </template>
                    <template v-if="index === bundle.applications.length - 1">
                      <div
                        v-if="bundle.selected"
                        class="absolute border-l-2 border-b-2 border-brand-green -top-5 left-2 h-12 w-4"
                      ></div>
                    </template>
                  </template>
                </div>

                <template v-if="getCurrentUserContext === 'applicant'">
                  <div class="w-1/5 px-1">
                    <p class="py-1 break-words">
                      {{ app.unit_address }}
                    </p>
                    <p class="text-sm italic text-brand-dark-gray">
                      - sub. to
                      {{ app.landlord.name }}
                    </p>
                  </div>

                  <div class="w-1/5 px-1">
                    <p class="py-1">{{ formatCreatedAt(app.created) }}</p>
                  </div>

                  <div class="w-5/12 px-1 flex items-center">
                    <template v-if="app.reports.length">
                      <div class="flex items-center flex-wrap">
                        <div
                          class="rounded-lg py-1 px-1 m-0.5 inline-block"
                          v-for="report in app.reports"
                          :key="`${app.id}-${report.id}`"
                          :class="[
                            report.status === 'ready'
                              ? 'bg-brand-background-light-blue'
                              : pendingStatuses.includes(report.status)
                              ? 'bg-brand-light-gray'
                              : report.status.includes('user_action_required')
                              ? 'bg-brand-background-light-yellow'
                              : errorStatuses.includes(report.status)
                              ? 'bg-brand-background-light-red'
                              : ''
                          ]"
                        >
                          <div class="flex flex-row">
                            <div class="flex items-center">
                              <CheckIcon
                                class="w-4 h-4 text-brand-blue mr-1"
                                v-if="report.status === 'ready'"
                              />
                              <ExclamationIcon
                                class="w-4 h-4 text-brand-yellow mr-1"
                                v-if="report.status.includes('user_action_required')"
                              />
                              <XIcon
                                class="w-4 h-4 text-brand-red mr-1"
                                v-if="['error', 'fatal'].includes(report.status)"
                              />
                              <ClockIcon
                                class="w-4 h-4 text-brand-mid-gray mr-1"
                                v-if="pendingStatuses.includes(report.status)"
                              />
                            </div>
                            <div class="flex items-center">
                              <p
                                :class="[
                                  'text-2xs capitalize',
                                  report.status === 'ready' ? 'text-brand-blue' : '',
                                  pendingStatuses.includes(report.status)
                                    ? 'text-brand-mid-gray'
                                    : '',
                                  report.status.includes('user_action_required')
                                    ? 'text-brand-yellow'
                                    : '',
                                  ['error', 'fatal'].includes(report.status) ? 'text-brand-red' : ''
                                ]"
                              >
                                {{ report.short_label }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                    <template v-else>
                      <div class="flex flex-row items-center py-1">
                        <div class="flex items-center mr-1"></div>
                        <div class="flex items-center">
                          <p class="italic">No reports ordered</p>
                        </div>
                      </div>
                    </template>
                  </div>

                  <div class="w-1/10 px-1 flex items-center">
                    <div class="flex items-center">
                      <router-link
                        :to="{ name: 'apply_edit', params: { id: app.id } }"
                        @click="$event.stopPropagation()"
                      >
                        <SecondaryButton :classes="'border-brand-mid-gray'"> Edit </SecondaryButton>
                      </router-link>
                    </div>
                  </div>

                  <div class="w-1/10 px-1 flex items-center">
                    <div class="flex items-center">
                      <router-link
                        :to="{
                          name: 'applicant_dashboard_view_application_application',
                          params: { id: app.id }
                        }"
                        @click="$event.stopPropagation()"
                      >
                        <SecondaryButton :classes="'border-brand-mid-gray'"> View </SecondaryButton>
                      </router-link>
                    </div>
                  </div>

                  <div class="w-1/5 px-1">
                    <div class="flex h-full w-full items-center">
                      <template v-if="['approved', 'rejected'].includes(app.status)">
                        <p class="capitalize">{{ app.status }}</p>
                      </template>
                      <template v-else>
                        <p>{{ app.status === "partial" ? "Incomplete" : "Submitted" }}</p>
                        <!-- <PrimaryButton>Request update</PrimaryButton> -->
                      </template>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <template
                    v-if="
                      ['dashboard_applicants_submitted', 'dashboard_applicants_archived'].includes(
                        $route.name
                      )
                    "
                  >
                    <div
                      class="flex items-center w-full"
                      :class="[
                        app.status === 'partial' && getCurrentUserContext !== 'applicant'
                          ? 'opacity-20 pointer-events-none'
                          : ''
                      ]"
                    >
                      <div class="w-1/4">
                        <div class="flex items-center">
                          <p class="py-1 box-border">
                            {{ app.applicant.name }}
                            <span v-if="app.is_new" class="ml-1 text-brand-red italic text-xs"
                              >new</span
                            >
                          </p>
                        </div>

                        <p class="text-sm italic text-brand-dark-gray">
                          {{ formatCreatedAt(app.created) }}
                          {{
                            getCurrentUser.landlord && getCurrentUser.landlord.in_company
                              ? `- sub. to ${app.landlord.name}`
                              : ""
                          }}
                        </p>
                      </div>

                      <div class="w-1/4">
                        <p class="py-1 box-border">
                          {{ app.unit_address }}
                        </p>
                      </div>

                      <div class="w-5/12 px-1">
                        <template v-if="app.reports.length">
                          <div
                            class="rounded-lg py-1 px-1 m-0.5 inline-block"
                            v-for="report in app.reports"
                            :key="`${app.id}-${report.id}`"
                            :class="[
                              report.status === 'ready'
                                ? 'bg-brand-background-light-blue'
                                : pendingStatuses.includes(report.status)
                                ? 'bg-brand-light-gray'
                                : report.status.includes('user_action_required')
                                ? 'bg-brand-background-light-yellow'
                                : errorStatuses.includes(report.status)
                                ? 'bg-brand-background-light-red'
                                : ''
                            ]"
                          >
                            <div class="flex flex-row">
                              <div class="flex items-center">
                                <CheckIcon
                                  class="w-4 h-4 text-brand-blue mr-1"
                                  v-if="report.status === 'ready'"
                                />
                                <ExclamationIcon
                                  class="w-4 h-4 text-brand-yellow mr-1"
                                  v-if="report.status.includes('user_action_required')"
                                />
                                <XIcon
                                  class="w-4 h-4 text-brand-red mr-1"
                                  v-if="['error', 'fatal'].includes(report.status)"
                                />
                                <ClockIcon
                                  class="w-4 h-4 text-brand-mid-gray mr-1"
                                  v-if="pendingStatuses.includes(report.status)"
                                />
                              </div>
                              <div class="flex items-center">
                                <p
                                  :class="[
                                    'text-2xs capitalize',
                                    report.status === 'ready' ? 'text-brand-blue' : '',
                                    pendingStatuses.includes(report.status)
                                      ? 'text-brand-mid-gray'
                                      : '',
                                    report.status.includes('user_action_required')
                                      ? 'text-brand-yellow'
                                      : '',
                                    ['error', 'fatal'].includes(report.status)
                                      ? 'text-brand-red'
                                      : ''
                                  ]"
                                >
                                  {{ report.short_label }}
                                </p>
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <router-link
                            :to="{ name: 'home_applicant_warning_message' }"
                            target="_blank"
                            v-if="app.status != 'partial' && !app.landlord_verified"
                          >
                            <div class="flex flex-col py-1">
                              <div class="flex flex-row">
                                <div class="flex items-center">
                                  <ExclamationIcon class="w-5 h-5 pr-1 text-brand-yellow" />
                                </div>
                                <div class="flex items-center">
                                  <p class="text-sm italic">No reports requested</p>
                                </div>
                              </div>
                              <div class="flex flex-col py-1">
                                <p class="text-brand-dark-gray text-xs italic">
                                  Applicants will be shown a warning.
                                </p>
                                <p class="text-brand-dark-gray text-xs italic">
                                  <router-link
                                    :to="{
                                      name: 'home_applicant_warning_message'
                                    }"
                                    class="text-brand-green text-left hover:underline"
                                    @click="$event.stopPropagation()"
                                    >Click here</router-link
                                  >
                                  to learn why we do this
                                </p>
                              </div>
                            </div>
                          </router-link>
                        </template>
                      </div>

                      <div class="w-1/4 px-1">
                        <div class="flex h-full">
                          <div
                            class="flex border-2 border-transparent hover:border-brand-mid-gray rounded hover:bg-brand-light-gray py-2 px-3"
                            @click.stop.prevent="openShareApplicationModal(app)"
                          >
                            <div class="flex items-center cursor-pointer">
                              <PaperPlaneIcon
                                :classes="'w-8 h-7 text-brand-green'"
                                :class="[
                                  app.status === 'partial' ? 'opacity-20 pointer-events-none' : ''
                                ]"
                              />
                            </div>
                            <div class="flex items-center ml-2">
                              <div class="-space-x-5 flex flex-row">
                                <template v-for="(share, index) in app.shares" :key="index">
                                  <Gravatar
                                    v-if="index < 3"
                                    :photo="share.shared_to.profile_photo"
                                    :zIndex="10 - index"
                                    :displayName="formatGravatar(share)"
                                    :color="`bg-palette-gravatar-${index + 1}`"
                                  />
                                </template>
                              </div>
                              <p class="ml-1 mt-5" v-if="app.shares.length > 3">...</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="w-1/5 px-1">
                        <div class="flex h-full items-center">
                          <div
                            class="border-2 border-transparent hover:border-brand-mid-gray rounded hover:bg-brand-light-gray py-2 px-3"
                          >
                            <ClockIcon
                              @click.prevent.stop="openHistoryModal(app)"
                              class="w-8 h-8 text-brand-green cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>

                      <div class="w-10 relative" @click.prevent.stop="goToViewApp(app)">
                        <p
                          class="text-brand-green absolute top-4 right-8 text-sm w-16"
                          v-if="showApp[app.id]"
                        >
                          View app.
                        </p>
                        <div
                          class="flex h-full justify-end items-center"
                          @mouseenter="toggleAppTip(app)"
                          @mouseleave="toggleAppTip(app)"
                        >
                          <ChevronRightIcon
                            class="w-8 h-8 cursor-pointer"
                            :class="[showApp[app.id] ? 'text-brand-green' : 'text-brand-dark-gray']"
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="$route.name === 'dashboard_applicants_invited'">
                    <div class="flex flex-col justify-center w-1/3">
                      <p class="truncate md:max-w-4xs lg:max-w-3xs xl:max-w-2xs">
                        {{ app.invitee }}
                      </p>
                      <a :href="`/${app.template_slug}`" class="text-sm text-brand-green">
                        Form - {{ app.form_name }}</a
                      >
                    </div>
                    <div class="flex items-center w-1/5">
                      <p class="py-1 italic">
                        {{ formatShortDate(app.created) }} @
                        {{ formatCreatedAt(app.created) }}
                      </p>
                    </div>
                    <div class="flex items-center w-1/5">
                      <template v-if="app.invitation_mail_status">
                        <p class="py-1 capitalize">
                          <!-- sent, opened, clicked -->
                          Invitation
                          {{ app.invitation_mail_status }}
                        </p>
                      </template>
                      <template v-else>
                        <p class="py-1 capitalize">
                          {{ app.status }}
                        </p>
                      </template>
                    </div>
                    <div class="flex items-center w-1/4">
                      <template v-if="app.invitation_application">
                        <PrimaryButton
                          @click.prevent.stop="
                            goToViewApp({ status: 'submitted', id: app.invitation_application })
                          "
                          classes="w-max"
                        >
                          <p class="text-white text-sm">View Submitted Application</p>
                        </PrimaryButton>
                      </template>
                    </div>
                  </template>

                  <template v-else>
                    <div class="w-1/4">
                      <div class="flex items-center">
                        <p class="py-1">
                          {{ app.applicant.name
                          }}<span v-if="app.is_new" class="ml-1 text-brand-red italic text-xs"
                            >new</span
                          >
                        </p>
                      </div>

                      <p class="text-sm italic text-brand-dark-gray">
                        {{ formatCreatedAt(app.created) }}
                        {{
                          getCurrentUser.landlord && getCurrentUser.landlord.in_company
                            ? `- sub. to ${app.landlord.name}`
                            : ""
                        }}
                      </p>
                    </div>

                    <div class="w-1/5 flex items-center">
                      <p class="text-md whitespace-pre-wrap">
                        {{ app.unit_address }}
                      </p>
                    </div>

                    <div class="w-1/5 flex flex-col justify-center">
                      <p>
                        <span class="text-sm">Received:</span>
                        {{ formatShortDate(app.created) }}
                      </p>
                      <p class="font-normal">
                        <span class="text-sm">Decided:</span>
                        {{ formatShortDate(app.decision_date) }}
                      </p>
                    </div>

                    <div class="w-1/5 flex items-center">
                      <div class="flex flex-col items-center">
                        <img
                          src="@/assets/icons/approve.svg"
                          class="w-6 h-6"
                          v-if="app.status === 'approved'"
                        />
                        <img
                          src="@/assets/icons/decline.svg"
                          class="w-6 h-6"
                          v-if="app.status === 'rejected'"
                        />
                        <h5 class="py-1 text-sm capitalize text-brand-dark-gray">
                          {{ app.status === "approved" ? "Selected" : "Declined" }}
                        </h5>
                      </div>
                    </div>

                    <div class="w-1/4">
                      <div
                        class="flex flex-row h-full items-center"
                        @click.stop.prevent="openShareApplicationModal(app)"
                      >
                        <div class="flex items-center cursor-pointer">
                          <PaperPlaneIcon :classes="'w-8 h-7 text-brand-green'" />
                        </div>
                        <div class="flex items-center ml-2">
                          <div class="-space-x-5 flex flex-row">
                            <template v-for="(share, index) in app.shares" :key="index">
                              <Gravatar
                                v-if="index < 3"
                                :photo="share.shared_to.profile_photo"
                                :zIndex="10 - index"
                                :displayName="formatGravatar(share)"
                                :color="`bg-palette-gravatar-${index + 1}`"
                              />
                            </template>
                          </div>
                          <p class="ml-1 mt-5" v-if="app.shares.length > 3">...</p>
                        </div>
                      </div>
                    </div>

                    <div class="w-1/5">
                      <div class="flex h-full items-center">
                        <ClockIcon
                          @click.stop.prevent="openHistoryModal(app)"
                          class="w-8 h-8 text-brand-green cursor-pointer"
                        />
                      </div>
                    </div>

                    <div class="w-10 relative" @click.stop.prevent="goToViewApp(app)">
                      <p
                        class="text-brand-green absolute top-4 right-8 text-sm w-16"
                        v-if="showApp[app.id]"
                      >
                        View app.
                      </p>
                      <div
                        class="flex h-full justify-end items-center"
                        @mouseenter="toggleAppTip(app)"
                        @mouseleave="toggleAppTip(app)"
                      >
                        <ChevronRightIcon
                          class="w-8 h-8 cursor-pointer"
                          :class="[showApp[app.id] ? 'text-brand-green' : 'text-brand-dark-gray']"
                        />
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </div>
            <!-- END DESKTOP APP ROW -->

            <!-- START MOBILE APP ROW -->
            <div
              v-if="app.status === 'partial' && getCurrentUserContext !== 'applicant'"
              class="md:hidden absolute left-3 bottom-5 z-30 w-64 py-2 px-2 text-xs mt-2 bg-brand-gray text-gray-100 px-1 rounded shadow-xl"
            >
              This application has not been completed by the applicant.
            </div>
            <div
              class="flex flex-col md:hidden w-full relative"
              :class="[
                app.status === 'partial' && getCurrentUserContext !== 'applicant'
                  ? 'opacity-20'
                  : ''
              ]"
            >
              <div class="mb-2" v-if="index !== 0">
                <div class="border border-brand-mid-gray"></div>
              </div>
              <div class="flex flex-col md:hidden w-full">
                <template v-if="getCurrentUserContext === 'applicant'">
                  <div class="flex flex-row justify-between">
                    <div class="flex flex-col">
                      <p class="py-0.5 text-base font-semibold">
                        {{ app.unit_address }}
                      </p>
                      <p class="text-sm italic text-brand-dark-gray">
                        - sub. to
                        {{ app.landlord.name }}
                      </p>
                      <p class="py-0.5 text-sm">
                        <span class="font-semibold">App Started: </span
                        ><span class="text-brand-dark-gray italic"
                          >{{ formatShortDate(app.created) }} @
                          {{ formatCreatedAt(app.created) }}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div class="flex flex-row justify-between py-3">
                    <div class="flex flex-col items-center">
                      <router-link
                        :to="{ name: 'apply_edit', params: { id: app.id } }"
                        class="text-brand-green text-sm hover:underline"
                        @click="$event.stopPropagation()"
                      >
                        Edit</router-link
                      >
                    </div>

                    <div
                      class="flex items-center"
                      @click.stop.prevent="goToViewApp(app)"
                      v-if="
                        !['sent to applicant', 'invitation bounced', 'partial'].includes(app.status)
                      "
                    >
                      <p class="text-brand-green">View</p>
                      <ChevronRightIcon class="w-5 h-5 text-brand-green" />
                    </div>
                  </div>
                </template>
                <template v-else>
                  <template
                    v-if="
                      ['dashboard_applicants_submitted', 'dashboard_applicants_archived'].includes(
                        $route.name
                      )
                    "
                  >
                    <div class="flex flex-row justify-between">
                      <div class="flex flex-col">
                        <div class="flex items-center">
                          <p class="py-0.5 text-base font-semibold">
                            {{ app.applicant.name }}
                            <span v-if="app.is_new" class="ml-1 text-brand-red italic text-xs"
                              >new</span
                            >
                          </p>
                        </div>
                        <p class="py-0.5 text-sm text-brand-dark-gray italic">
                          {{ app.unit_address }}
                        </p>
                      </div>
                      <div class="flex flex-row">
                        <div class="flex items-center">
                          <PaperPlaneIcon
                            :classes="'w-7 h-7 text-brand-green'"
                            @click.stop.prevent="openShareApplicationModal(app)"
                          />
                        </div>

                        <div
                          class="flex items-center pl-3 pr-1"
                          @click.stop.prevent="openMobileModal(app)"
                        >
                          <DotsVerticalIcon class="w-7 h-7 text-brand-mid-gray" />
                        </div>
                      </div>
                    </div>
                    <div class="flex flex-row justify-between py-3">
                      <template v-if="app.reports.length">
                        <div class="flex items-center">
                          <div
                            class="py-1 px-1.5 rounded text-sm"
                            :class="[
                              app.reports.filter((r) => r.status === 'ready').length > 0
                                ? 'bg-brand-background-light-blue text-brand-blue'
                                : 'bg-brand-light-gray text-brand-mid-gray'
                            ]"
                          >
                            {{ app.reports.filter((r) => r.status === "ready").length }}/{{
                              app.reports.length
                            }}
                            reports ready
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <router-link
                          :to="{ name: 'home_applicant_warning_message' }"
                          target="_blank"
                        >
                          <div class="flex flex-col py-1">
                            <div class="flex flex-row">
                              <div class="flex items-center">
                                <ExclamationIcon class="w-5 h-5 pr-1 text-brand-yellow" />
                              </div>
                              <div class="flex items-center">
                                <p class="text-sm italic">No reports requested</p>
                              </div>
                            </div>
                            <div class="flex flex-col py-1">
                              <p class="text-brand-dark-gray text-xs italic">
                                Applicants will be shown a warning.
                              </p>
                              <p class="text-brand-dark-gray text-xs italic">
                                <router-link
                                  :to="{
                                    name: 'home_applicant_warning_message'
                                  }"
                                  class="text-brand-green text-left hover:underline"
                                  @click="$event.stopPropagation()"
                                  >Click here</router-link
                                >
                                to learn why we do this
                              </p>
                            </div>
                          </div>
                        </router-link>
                      </template>
                      <div class="flex items-center" @click.stop.prevent="goToViewApp(app)">
                        <p class="text-brand-green">View</p>
                        <ChevronRightIcon class="w-5 h-5 text-brand-green" />
                      </div>
                    </div>
                  </template>

                  <template v-else-if="$route.name === 'dashboard_applicants_invited'">
                    <div class="flex flex-row justify-between py-1">
                      <div class="flex flex-col">
                        <p class="py-0.5 text-base font-semibold">
                          {{ app.invitee }}
                        </p>
                        <p class="py-0.5 text-sm text-brand-dark-gray italic">
                          {{ formatDate(app.created) }} @
                          {{ formatCreatedAt(app.created) }}
                        </p>
                      </div>
                      <div class="flex flex-row">
                        <div class="flex items-center justify-end">
                          <template v-if="app.invitation_application">
                            <div class="flex items-center w-2/3">
                              <PrimaryButton
                                @click.prevent.stop="
                                  goToViewApp({
                                    status: 'submitted',
                                    id: app.invitation_application
                                  })
                                "
                              >
                                <p class="text-white text-sm">View Submitted Application</p>
                              </PrimaryButton>
                            </div>
                          </template>
                          <template v-else>
                            <template v-if="app.invitation_mail_status">
                              <p class="py-1 capitalize">
                                <!-- sent, opened, clicked -->
                                Invitation
                                {{ app.invitation_mail_status }}
                              </p>
                            </template>
                            <template v-else>
                              <p class="py-1 capitalize">
                                {{ app.status }}
                              </p>
                            </template>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <div class="flex flex-row justify-between">
                      <div class="flex flex-col">
                        <p class="py-0.5 text-base font-semibold">
                          {{ app.applicant.name }}
                          <span v-if="app.is_new" class="ml-1 text-brand-red italic text-xs"
                            >new</span
                          >
                        </p>
                        <p class="py-1 text-sm">
                          {{ app.unit_address }}
                        </p>
                        <p class="py-0.5 text-sm">
                          <span class="font-semibold">Rec: </span
                          ><span class="text-brand-dark-gray italic"
                            >{{ formatShortDate(app.created) }} @
                            {{ formatCreatedAt(app.created) }}</span
                          >
                        </p>
                        <p class="py-0.5 text-sm">
                          <span class="font-semibold">Dec: </span
                          ><span class="text-brand-dark-gray italic"
                            >{{ formatShortDate(app.decision_date) }} @
                            {{ formatCreatedAt(app.decision_date) }}</span
                          >
                        </p>
                      </div>
                      <div class="flex flex-row">
                        <div class="flex items-center mr-8">
                          <PaperPlaneIcon
                            :classes="'w-7 h-7 text-brand-green'"
                            @click.stop.prevent="openShareApplicationModal(app)"
                          />
                        </div>

                        <div class="flex items-center" @click.stop.prevent="openMobileModal(app)">
                          <DotsVerticalIcon class="w-7 h-7 text-brand-mid-gray" />
                        </div>
                      </div>
                    </div>

                    <div class="flex flex-row justify-between py-3">
                      <div class="flex flex-col items-center">
                        <img
                          src="@/assets/icons/approve.svg"
                          class="w-5 h-5"
                          v-if="app.status === 'approved'"
                        />
                        <img
                          src="@/assets/icons/decline.svg"
                          class="w-5 h-5"
                          v-if="app.status === 'rejected'"
                        />
                        <h5 class="py-1 text-xs capitalize text-brand-dark-gray">
                          {{ app.status === "approved" ? "Selected" : "Declined" }}
                        </h5>
                      </div>

                      <div class="flex items-center" @click.stop.prevent="goToViewApp(app)">
                        <p class="text-brand-green">View</p>
                        <ChevronRightIcon class="w-5 h-5 text-brand-green" />
                      </div>
                    </div>
                  </template>
                </template>
              </div>
            </div>
            <!-- END MOBILE APP ROW -->
          </div>
        </div>
      </div>

      <div class="flex justify-end">
        <Pagination
          :current="getApplicationBundleCurrentPage"
          :total="getApplicationBundleTotalCount"
          :per-page="25"
          @page-changed="loadBundles"
        />
      </div>
    </template>
    <!-- END HAS BUNDLES DISPLAY -->

    <!-- START NO BUNDLES TO DISPLAY -->
    <template v-if="!getApplicationBundles || !getApplicationBundles.length">
      <template v-if="getApplicationBundleSearchTerm">
        <div class="flex flex-col">
          <p class="py-1 px-2 text-sm md:text-base">
            No search results matching "{{ getApplicationBundleSearchTerm }}", please try a
            different search.
          </p>
          <div class="flex items-center px-2">
            <div
              class="md:hidden mt-1 py-0.5 px-1 flex items-center bg-white border border-brand-mid-gray rounded"
              @click="clearSearch"
            >
              <p class="text-brand-dark-gray text-sm">clear</p>
            </div>
          </div>
        </div>
      </template>
      <template v-else-if="filterApplied">
        <div class="flex flex-col">
          <p class="py-1 px-2 text-sm md:text-base">
            No results matching the filters you've applied. Please try a different search.
          </p>
        </div>
      </template>
      <template v-else-if="status === 'decided' && getApplicationBundleStatus !== 'decided'">
        <div class="flex flex-col">
          <p class="py-1 px-2 text-sm md:text-base">
            No {{ getApplicationBundleStatus }} applicants. Please try a different search.
          </p>
        </div>
      </template>
      <template v-else>
        <div class="flex flex-col py-5">
          <div class="w-1/2 md:w-1/4 mx-auto">
            <Lottie :animationData="statusAnimationMap[status]" />
          </div>
          <template v-if="status === 'archived'">
            <h2 class="py-1 px-2 pb-5 text-xl md:text-3xl text-center">
              Looks like you don't have any archived applicants
            </h2>
            <p class="py-1 px-2 text-sm md:text-base text-center">
              Applicants will be displayed here after you've archived applicants you've received.
            </p>
          </template>

          <template v-else-if="status === 'decided'">
            <h2 class="py-1 px-2 pb-5 text-xl md:text-3xl text-center">
              Looks like you don't have any decided applicants
            </h2>
            <p class="py-1 px-2 text-sm md:text-base text-center">
              Applicants will be displayed here after you've decided applicants you've received.
            </p>
          </template>

          <template v-else-if="status === 'invited'">
            <h2 class="py-1 px-2 pb-5 text-xl md:text-3xl text-center">
              Looks like you don't have any invited applicants.
            </h2>
            <p class="py-1 px-2 text-sm md:text-base text-center">
              Head to the
              <router-link :to="{ name: 'dashboard_forms' }" class="text-brand-green cursor-pointer"
                >Forms tab</router-link
              >
              to send out a form to receive your first applicant.
            </p>
          </template>

          <template v-else>
            <h2 class="py-1 px-2 pb-5 text-xl md:text-3xl text-center">
              {{
                getCurrentUser && getCurrentUser.active_app_count > 0
                  ? "Looks like you don't have recently submitted applicants."
                  : "Looks like you don't have any applicants."
              }}
            </h2>
            <template v-if="getCurrentUser && getCurrentUser.active_app_count === 0">
              <p class="py-1 px-2 text-sm md:text-base text-center">
                Head to the
                <router-link
                  :to="{ name: 'dashboard_forms' }"
                  class="text-brand-green cursor-pointer"
                  >Forms tab</router-link
                >
                to send out a form to receive your first applicant.
              </p>
            </template>
          </template>
        </div>
      </template>
    </template>
    <!-- END NO BUNDLES TO DISPLAY -->
  </template>
  <!-- END DISPLAY BUNDLE TABLE -->

  <template v-if="selectedApp">
    <MobileAdditionalActionModal
      :showModal="showMobileModal"
      @close="closeMobileModal"
      :app="selectedApp"
      @history="openHistoryModal"
      @notes="openNotesModal"
      @share="openShareApplicationModal"
    />
    <ShareApplicationModal
      :app="selectedApp"
      :showModal="showShareApplicationModal"
      @close="closeShareApplicationModal"
    />
    <ApplicationHistoryModal
      :showModal="showHistoryModal"
      @close="closeHistoryModal"
      :app="selectedApp"
    />
    <NotesModal :app="selectedApp" :showModal="showNotesModal" @close="closeNotesModal" />
  </template>
  <ActionToast ref="bundleTableToast" />
</template>
<script>
import ApplicantsEmpty from "@/assets/animations/applicants-empty.json"
import ArchivedEmpty from "@/assets/animations/archived-empty.json"
import DecidedEmpty from "@/assets/animations/decided-empty.json"
import InvitedEmpty from "@/assets/animations/invited.json"

import {
  CheckIcon,
  ClockIcon,
  ExclamationIcon,
  XIcon,
  ChevronRightIcon,
  DotsVerticalIcon
} from "@heroicons/vue/outline"

import MobileAdditionalActionModal from "@/views/dashboard/view_application/components/MobileAdditionalActionModal.vue"
import ApplicationHistoryModal from "@/views/dashboard/view_application/components/ApplicationHistoryModal.vue"
import NotesModal from "@/views/dashboard/view_application/components/NotesModal.vue"
import ShareApplicationModal from "@/views/dashboard/view_application/components/ShareApplicationModal.vue"

import { APPLICATION_BUNDLE_GET_LIST } from "@/store/types"
import { mapGetters } from "vuex"
import moment from "moment"
export default {
  components: {
    CheckIcon,
    ClockIcon,
    ExclamationIcon,
    XIcon,
    ChevronRightIcon,
    DotsVerticalIcon,
    MobileAdditionalActionModal,
    ApplicationHistoryModal,
    NotesModal,
    ShareApplicationModal
  },
  props: {
    selectAll: {
      type: Boolean
    }
  },
  emits: ["select"],
  computed: {
    ...mapGetters([
      "getApplicationBundles",
      "getCurrentUser",
      "getApplicationBundleSearchTerm",
      "isApplicationBundleLoading",
      "getApplicationBundleCurrentPage",
      "getApplicationBundleTotalCount",
      "getCurrentUserContext",
      "getApplicationBundleFilters",
      "getApplicationBundleStatus"
    ]),
    filterAppliedCount() {
      if (!this.filterApplied) {
        return 0
      }
      return this.getApplicationBundleFilters.reports.length
    },
    filterApplied() {
      if (!this.getApplicationBundleFilters) {
        return false
      }
      return !!this.getApplicationBundleFilters.reports
    }
  },
  watch: {
    selectAll: {
      handler(val) {
        let selected = []
        if (val) {
          selected = Object.values(this.formattedBundles)
            .flat()
            .map((bundle) => {
              bundle.selected = true
              return bundle
            })
            .filter(Boolean)
        } else {
          Object.values(this.formattedBundles)
            .flat()
            .map((bundle) => {
              bundle.selected = false
              return bundle
            })
        }

        this.$emit("select", selected)
      }
    },
    getApplicationBundles: {
      handler(val) {
        // Group bundles by date created
        const formattedBundles = {}

        if (val && val.length) {
          // Clone the bundle data to format
          const copiedBundles = JSON.parse(JSON.stringify(val))

          copiedBundles.map((bundle) => {
            const created = moment(bundle.created)

            if (!formattedBundles[created.format("MM-DD-YYYY")]) {
              formattedBundles[created.format("MM-DD-YYYY")] = []
            }

            bundle.applications = bundle.applications.map((app) => {
              app.shares = bundle.shares
              return app
            })
            formattedBundles[created.format("MM-DD-YYYY")].push(bundle)
          })
        }
        this.formattedBundles = formattedBundles
      },
      deep: true,
      immediate: true
    }
  },
  data() {
    return {
      formattedBundles: null,
      showApp: {},
      showMobileModal: false,
      selectedApp: null,
      showHistoryModal: false,
      showNotesModal: false,
      showShareApplicationModal: false,
      pendingStatuses: ["sent_payment_request", "new", "pending", "requested"],
      errorStatuses: ["error", "fatal"],
      status: "",
      statusAnimationMap: {
        submitted: ApplicantsEmpty,
        invited: InvitedEmpty,
        archived: ArchivedEmpty,
        decided: DecidedEmpty
      }
    }
  },
  async mounted() {
    const that = this
    if (that.getCurrentUserContext === "landlord") {
      this.status = this.$route.name.split("dashboard_applicants_")[1]
    }

    await this.loadBundles()
  },
  methods: {
    async loadBundles(page = 1) {
      await this.$store.dispatch(APPLICATION_BUNDLE_GET_LIST, {
        page,
        status: this.status
      })
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth"
        })
      }, 1000)
    },
    formatShortDate(date) {
      return moment(date).format("M/D/YY")
    },
    formatDate(date) {
      return moment(date, "MM-DD-YYYY").format("dddd, M/D/YYYY")
    },
    formatCreatedAt(date) {
      return `${moment(date).format("h:mma")} CST`
    },
    formatGravatar(share) {
      if (share.shared_to.name) {
        if (share.shared_to.name.split(" ").length > 1) {
          return `${share.shared_to.name.split(" ")[0].trim()[0]}${
            share.shared_to.name.split(" ")[1].trim()[0]
          }`
        } else {
          return `${share.shared_to.name.substring(0, 2)}`
        }
      } else if (share.shared_to.display_email) {
        return share.shared_to.display_email.substring(0, 2)
      } else {
        return "N/A"
      }
    },
    goToViewApp(app) {
      if (["sent to applicant", "invitation bounced", "partial"].includes(app.status)) {
        return
      }

      if (this.getCurrentUserContext === "applicant") {
        this.$router.push({
          name: "applicant_dashboard_view_application_application",
          params: { id: app.id }
        })
      } else {
        this.$router.push({
          name: "dashboard_view_application_summary",
          params: { id: app.id }
        })
      }
    },
    toggleAppTip(app) {
      this.showApp[app.id] = !this.showApp[app.id]
    },
    openMobileModal(app) {
      if (["sent to applicant", "invitation bounced", "partial"].includes(app.status)) {
        return
      }

      this.showMobileModal = true
      this.selectedApp = app
    },
    closeMobileModal() {
      this.showMobileModal = false

      //This is required for smooth leave transition
      const that = this
      setTimeout(() => {
        that.selectedApp = null
      }, 1000)
    },
    openHistoryModal(app) {
      this.showHistoryModal = true
      if (app) this.selectedApp = app

      if (this.showMobileModal) this.showMobileModal = false
    },
    closeHistoryModal() {
      this.showHistoryModal = false
      this.selectedApp = null
    },
    openNotesModal(app) {
      this.showNotesModal = true
      if (app) this.selectedApp = app

      if (this.showMobileModal) this.showMobileModal = false
    },
    closeNotesModal() {
      this.showNotesModal = false
      this.selectedApp = null
    },
    openShareApplicationModal(app) {
      if (["sent to applicant", "invitation bounced", "partial"].includes(app.status)) {
        return
      }

      this.showShareApplicationModal = true
      if (app) this.selectedApp = app

      if (this.showMobileModal) this.showMobileModal = false
    },
    closeShareApplicationModal(reload) {
      this.showShareApplicationModal = false
      this.selectedApp = null

      if (reload) {
        this.$refs.bundleTableToast.show({
          type: "success",
          hasClose: true,
          content: "Application shared",
          icon: "check"
        })
      }
    },
    selectBundle(bundle) {
      bundle.selected = !bundle.selected
      const selected = Object.values(this.formattedBundles)
        .flat()
        .map((bundle) => (bundle.selected ? bundle : ""))
        .filter(Boolean)
      this.$emit("select", selected)
    },
    async clearSearch() {
      await this.loadBundles()
    }
  }
}
</script>
